import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { scrollToId } from "@/lib/smooth-scroll";

gsap.registerPlugin(ScrollTrigger);

const PHRASES = [
  { num: "01", text: "KI verstehen." },
  { num: "02", text: "Abläufe automatisieren." },
  { num: "03", text: "Team entlasten." },
];

const HeadlineLine = ({ text }) => (
  <span className="block">
    {text.split(" ").map((word, i) => (
      <span key={i} className="hero-word">
        <span>{word}</span>
      </span>
    ))}
  </span>
);

export const Hero = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const headlineRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      video.loop = true;
      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        },
        { threshold: 0.05 }
      );
      io.observe(video);
      return () => io.disconnect();
    }

    // Scroll-scrubbed video: playback is driven by scroll progress.
    video.pause();
    const isTouch = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    const pinDistance = isTouch ? "+=130%" : "+=300%";
    const ctx = gsap.context(() => {
      const proxy = { p: 0 };
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: pinDistance,
          pin: true,
          scrub: 0.7,
          anticipatePin: 1,
        },
      });
      tl.to(
        proxy,
        {
          p: 1,
          duration: 1,
          ease: "none",
          onUpdate: () => {
            const d = video.duration;
            if (d && Number.isFinite(d)) {
              const t = proxy.p * d;
              if (Math.abs(video.currentTime - t) > 0.01) {
                video.currentTime = t;
              }
            }
          },
        },
        0
      )
        .to(video, { scale: 1.06, duration: 1, ease: "none" }, 0)
        .to(
          headlineRef.current,
          { yPercent: -55, autoAlpha: 0, duration: 0.35, ease: "power1.in" },
          0
        )
        .to(overlayRef.current, { opacity: 1, duration: 0.25, ease: "none" }, 0.75);

      // Story phrases appearing sequentially during the scrub
      const slots = [
        [0.34, 0.52],
        [0.54, 0.72],
        [0.74, 0.92],
      ];
      gsap.utils.toArray(".hero-phrase").forEach((el, i) => {
        const [a, b] = slots[i];
        const span = b - a;
        tl.fromTo(
          el,
          { autoAlpha: 0, y: 70, scale: 0.96 },
          { autoAlpha: 1, y: 0, scale: 1, duration: span * 0.4, ease: "power2.out" },
          a
        ).to(
          el,
          { autoAlpha: 0, y: -70, duration: span * 0.35, ease: "power2.in" },
          b - span * 0.35
        );
      });
    }, sectionRef);

    const onMeta = () => ScrollTrigger.refresh();
    if (video.readyState < 1) {
      video.addEventListener("loadedmetadata", onMeta, { once: true });
    }

    return () => {
      video.removeEventListener("loadedmetadata", onMeta);
      ctx.revert();
    };
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".hero-word > span",
        { yPercent: 115 },
        { yPercent: 0, duration: 1.1, ease: "power4.out", stagger: 0.07, delay: 0.35 }
      );
      gsap.fromTo(
        ".hero-fade",
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out", stagger: 0.15, delay: 0.9 }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-section-idx="0"
      className="relative h-screen w-full overflow-hidden"
      data-testid="hero-section"
    >
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover will-change-transform"
        poster="/hero-poster.jpg"
        muted
        playsInline
        preload="auto"
        data-testid="hero-video"
      >
        <source src="/hero-scrub.webm" type="video/webm" />
        <source src="/hero-scrub.mp4" type="video/mp4" />
      </video>
      <div
        ref={overlayRef}
        className="hero-overlay absolute inset-0 opacity-0 pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1E1B18]/25 pointer-events-none" />

      <div className="hero-phrases pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
        {PHRASES.map((phrase, i) => (
          <div
            key={phrase.num}
            className="hero-phrase absolute px-6 text-center opacity-0"
            data-testid={`hero-phrase-${i}`}
          >
            <p className="eyebrow mb-4 text-[#E8C887] sm:mb-5">{phrase.num} / 03</p>
            <p
              className="font-display text-3xl text-[#F5F1E8] sm:text-6xl lg:text-7xl"
              style={{ textShadow: "0 2px 40px rgba(30,27,24,0.5)" }}
            >
              {phrase.text}
            </p>
          </div>
        ))}
      </div>

      <div
        ref={headlineRef}
        className="relative z-10 flex h-full flex-col justify-end px-6 pb-24 sm:px-10 lg:px-24 lg:pb-28"
      >
        <p className="hero-fade eyebrow mb-6 text-[#F5F1E8]/80" data-testid="hero-eyebrow">
          001 / 006 — Digitale KI-Agentur
        </p>
        <h1
          className="font-display text-5xl text-[#F5F1E8] sm:text-7xl lg:text-[7rem] max-w-5xl"
          data-testid="hero-headline"
        >
          <HeadlineLine text="Intelligenz, die" />
          <HeadlineLine text="für Sie arbeitet." />
        </h1>
        <p className="hero-fade mt-7 max-w-md text-base text-[#F5F1E8]/75 sm:text-lg">
          Maßgeschneiderte KI-Lösungen für den deutschen Mittelstand —
          fundiert konzipiert, sorgfältig umgesetzt, messbar wirksam.
        </p>
        <div className="hero-fade mt-9">
          <button
            onClick={() => scrollToId("#kontakt")}
            className="btn-primary"
            data-testid="hero-cta-button"
          >
            Kostenloses Erstgespräch
          </button>
        </div>
      </div>
    </section>
  );
};
